import type { PendingToolCall } from "@app/components/assistant/conversation/types";
import {
  removePendingToolCallForAction,
  upsertPendingToolCall,
} from "@app/hooks/useAgentMessageStream";
import { useEventSource } from "@app/hooks/useEventSource";
import { getActionOneLineLabel } from "@app/lib/api/assistant/activity_steps";
import type { AgentMessageEvents } from "@app/lib/api/assistant/streaming/types";
import {
  getAgentLoopEventId,
  isTerminalAgentLoopEvent,
} from "@app/lib/client/agent_loop_stream";
import type { InlineActivityStep } from "@app/types/assistant/conversation";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useReducer } from "react";

type ChildAgentStreamStatus = "created" | "streaming" | "done" | "error";

interface ChildAgentStreamReducerState {
  response: string;
  cotBuffer: string;
  status: ChildAgentStreamStatus;
  inlineActivitySteps: InlineActivityStep[];
  pendingToolCalls: PendingToolCall[];
}

interface ChildAgentStreamResult {
  response: string;
  isStreamingResponse: boolean;
  inlineActivitySteps: InlineActivityStep[];
  pendingToolCalls: PendingToolCall[];
  activeCotContent: string;
  isDone: boolean;
  isError: boolean;
}

type ChildAgentStreamEvent =
  | AgentMessageEvents
  | { type: "end-of-stream" }
  | { type: "reset" }
  | { type: "stream_error" };

const initialState: ChildAgentStreamReducerState = {
  response: "",
  cotBuffer: "",
  status: "created",
  inlineActivitySteps: [],
  pendingToolCalls: [],
};

// TODO(tasks#9287): unlike useAgentMessageStream, this reducer does not dedupe
// Temporal activity retries (no per-step traceId tracking / step-scoped drop,
// no seenEventIds replay guard), so a retried sub-agent step can duplicate
// inline steps mid-stream. It self-heals at completion via contentView.
function childAgentStreamReducer(
  state: ChildAgentStreamReducerState,
  event: ChildAgentStreamEvent
): ChildAgentStreamReducerState {
  switch (event.type) {
    case "reset":
      return initialState;

    case "generation_tokens": {
      if (event.classification === "tokens") {
        return {
          ...state,
          response: state.response + event.text,
          status: "streaming",
        };
      }
      if (event.classification === "chain_of_thought") {
        return {
          ...state,
          cotBuffer: state.cotBuffer + event.text,
          status: "streaming",
        };
      }
      // opening_delimiter and closing_delimiter we don't rely on.
      return state;
    }

    case "tool_params": {
      // Flush accumulated CoT to a thinking step when the tool starts executing.
      const trimmedCot = state.cotBuffer.trim();
      const newSteps: InlineActivityStep[] = trimmedCot
        ? [
            ...state.inlineActivitySteps,
            {
              type: "thinking" as const,
              content: trimmedCot,
              id: `thinking-${event.created}`,
            },
          ]
        : state.inlineActivitySteps;
      return {
        ...state,
        cotBuffer: "",
        inlineActivitySteps: newSteps,
      };
    }

    case "tool_call_started": {
      return {
        ...state,
        pendingToolCalls: upsertPendingToolCall(state.pendingToolCalls, {
          toolName: event.toolName,
          toolCallId: event.toolCallId,
          toolCallIndex: event.toolCallIndex,
        }),
      };
    }

    case "agent_action_success": {
      const { action } = event;
      return {
        ...state,
        pendingToolCalls: removePendingToolCallForAction(
          state.pendingToolCalls,
          action
        ),
        inlineActivitySteps: [
          ...state.inlineActivitySteps,
          {
            type: "action" as const,
            label: getActionOneLineLabel(action, "done"),
            id: `action-${action.id}`,
            actionId: action.sId,
            internalMCPServerName: action.internalMCPServerName,
            toolName: action.toolName,
          },
        ],
      };
    }

    case "agent_message_gracefully_stopped":
    case "agent_message_success": {
      // Trust the server-rendered content view: it is computed from the same
      // persisted step contents reload uses, so it matches reload exactly (same
      // as the top-level stream). Fall back to the locally flushed view only if
      // an older server omitted it during a deploy window.
      if (event.contentView) {
        return {
          response: event.contentView.content ?? state.response,
          cotBuffer: "",
          inlineActivitySteps: event.contentView.activitySteps,
          pendingToolCalls: [],
          status: "done",
        };
      }
      // Flush any remaining CoT buffer as a final thinking step.
      const trimmedCot = state.cotBuffer.trim();
      const finalSteps: InlineActivityStep[] = trimmedCot
        ? [
            ...state.inlineActivitySteps,
            {
              type: "thinking" as const,
              content: trimmedCot,
              id: `thinking-final-${event.created}`,
            },
          ]
        : state.inlineActivitySteps;
      return {
        response: event.message.content ?? state.response,
        cotBuffer: "",
        inlineActivitySteps: finalSteps,
        pendingToolCalls: [],
        status: "done",
      };
    }

    case "agent_error":
    case "tool_error":
    case "stream_error":
      return { ...state, cotBuffer: "", pendingToolCalls: [], status: "error" };

    case "agent_generation_cancelled":
    case "end-of-stream":
      return { ...state, cotBuffer: "", pendingToolCalls: [], status: "done" };

    // Events we don't use for the child stream display.
    case "tool_notification":
    case "agent_context_pruned":
    case "agent_credit_spend_checkpoint_updated":
    case "tool_approve_execution":
    case "tool_personal_auth_required":
    case "tool_file_auth_required":
    case "tool_ask_user_question":
      return state;

    default:
      assertNeverAndIgnore(event);
      return state;
  }
}

interface UseChildAgentStreamParams {
  childStreamIds: {
    conversationId: string;
    agentMessageId: string;
  } | null;
  owner: LightWorkspaceType;
  disabled: boolean;
}

/**
 * @cc [owner:id13,label:react;concurrency] child-stream-identity-and-completion
 * Changing the conversation or message ID MUST reset displayed stream state. An end-of-stream
 * sentinel or terminal transport failure MUST stop the subscription.
 */
export function useChildAgentStream({
  childStreamIds,
  owner,
  disabled,
}: UseChildAgentStreamParams): ChildAgentStreamResult {
  const streamKey = `${childStreamIds?.conversationId}:${childStreamIds?.agentMessageId}`;
  const [streamState, dispatch] = useReducer(
    (
      previous: { streamKey: string; state: ChildAgentStreamReducerState },
      event: { streamKey: string; data: ChildAgentStreamEvent }
    ) => {
      const isEventForStoredStream = previous.streamKey === event.streamKey;
      const state = isEventForStoredStream ? previous.state : initialState;

      return {
        streamKey: event.streamKey,
        state: childAgentStreamReducer(state, event.data),
      };
    },
    { streamKey, state: initialState }
  );
  const hasStreamChanged = streamState.streamKey !== streamKey;
  const state = hasStreamChanged ? initialState : streamState.state;
  if (hasStreamChanged) {
    dispatch({ streamKey, data: { type: "reset" } });
  }

  const buildEventSourceURL = useCallback(
    (lastEvent: string | null) => {
      if (!childStreamIds || disabled) {
        return null;
      }
      const { conversationId, agentMessageId } = childStreamIds;
      const baseUrl = `/api/sse/w/${owner.sId}/assistant/conversations/${conversationId}/messages/${agentMessageId}/events`;
      const lastEventId = getAgentLoopEventId(lastEvent);
      return baseUrl + "?lastEventId=" + encodeURIComponent(lastEventId);
    },
    [childStreamIds, owner.sId, disabled]
  );

  const onEventCallback = useCallback(
    (eventStr: string) => {
      const eventPayload: {
        eventId: string;
        data: ChildAgentStreamEvent;
      } = JSON.parse(eventStr);

      dispatch({ streamKey, data: eventPayload.data });
    },
    [streamKey]
  );

  const buildLongPollURL = useCallback(
    (lastEvent: string | null) => {
      if (!childStreamIds || disabled) {
        return null;
      }
      const { conversationId, agentMessageId } = childStreamIds;
      return `/api/sse/w/${owner.sId}/assistant/conversations/${conversationId}/messages/${agentMessageId}/events?transport=poll&lastEventId=${encodeURIComponent(getAgentLoopEventId(lastEvent))}`;
    },
    [childStreamIds, disabled, owner.sId]
  );

  const isStreamDone = state.status === "done" || state.status === "error";

  useEventSource(
    buildEventSourceURL,
    onEventCallback,
    `child-agent-${childStreamIds?.agentMessageId}`,
    {
      workspaceId: owner.sId,
      buildLongPollURL,
      isTerminalEvent: isTerminalAgentLoopEvent,
      onTerminalError: () =>
        dispatch({ streamKey, data: { type: "stream_error" } }),
      isReadyToConsumeStream:
        childStreamIds !== null && !isStreamDone && !disabled,
      telemetryContext: {
        sseKind: "child_agent",
        conversationId: childStreamIds?.conversationId ?? null,
        messageId: childStreamIds?.agentMessageId ?? null,
      },
    }
  );

  const isStreaming = state.status === "streaming";

  return {
    response: state.response,
    isStreamingResponse: isStreaming && state.response.length > 0,
    inlineActivitySteps: state.inlineActivitySteps,
    pendingToolCalls: state.pendingToolCalls,
    activeCotContent: state.cotBuffer,
    isDone: isStreamDone,
    isError: state.status === "error",
  };
}
